import clsx from "clsx";
import React, { useRef, useEffect, useState, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
// material ui components
import Link from "@mui/material/Link";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import CircularProgress from "@mui/material/CircularProgress";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
// material ui icons
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import LikedIcon from "@mui/icons-material/Star";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LikedIconBorder from "@mui/icons-material/StarBorder";
// jarr
import { removeClusterSelection, showCluster } from "../slice";
import doEditCluster from "../../../hooks/doEditCluster";
import doFetchCluster from "../../../hooks/doFetchCluster";
import useStyles from "./style";
import { changeReadCount } from "../../feedlist/slice";
import ClusterIcon from "../../../components/ClusterIcon";
import Articles from "./Articles";
import useSwipeGesture from "../../../hooks/useSwipeGesture";
import { appBarHeight } from "../../../const";

const getCluster = (state, props) => state.clusters.clusters[props.index];

const makeMapStateToProps = () => {
  const mapStateToProps = (state, props) => {
    const cluster = getCluster(state, props);
    // Guard against undefined cluster (can happen during loading state transitions)
    if (!cluster) {
      return {
        expanded: false,
        unreadOnClose: !state.clusters.filters.filter,
        icons: state.feeds.icons,
        showContent: false,
        cluster: null,
        doShow: false,
      };
    }
    return { expanded: cluster.id === state.clusters.requestedClusterId,
             unreadOnClose: !state.clusters.filters.filter,
             icons: state.feeds.icons,
             showContent: state.clusters.loadedCluster.id === cluster.id,
             cluster,
             doShow: showCluster(cluster, state.clusters.requestedClusterId,
                                 state.clusters.filters.filter),
    };
  };
  return mapStateToProps;
}

const mapDispatchToProps = (dispatch) => ({
  handleClickOnPanel(e, cluster, unreadOnClose, expanded) {
    if (!expanded) {
      // panel is folded, we fetch the cluster
      dispatch(doFetchCluster(cluster.id));
      dispatch(changeReadCount({
        feedsId: cluster["feeds_id"],
        action: "read" }));
    } else if (unreadOnClose) {
      // panel is expanded and the filters implies
      // we have to mark cluster as unread
      dispatch(removeClusterSelection());
      dispatch(doEditCluster(cluster.id,
                             { read: false, "read_reason": null }));
      dispatch(changeReadCount(
          { feedsId: cluster["feeds_id"],
            action: "unread" }));
    } else {
      // filters says everybody is displayed
      // so we"re not triggering changes in cluster list
      dispatch(removeClusterSelection());
    }
  },
  toggleRead(e, cluster) {
    e.stopPropagation();
    const payload = { feedsId: cluster["feeds_id"] };
    if (cluster.read && !e.target.checked) {
      dispatch(doEditCluster(cluster.id, { read: false, "read_reason": null }));
      dispatch(changeReadCount({ ...payload, action: "unread" }));
    } else if (!cluster.read && e.target.checked) {

      dispatch(doEditCluster(cluster.id, { read: true, "read_reason": "marked" }));
      dispatch(changeReadCount({ ...payload, action: "read" }));
    }
  },
  toggleLiked(e, clusterId) {
    e.stopPropagation();
    dispatch(doEditCluster(clusterId, { liked: e.target.checked }));
  },
  markAsRead(cluster) {
    if (!cluster.read) {
      dispatch(doEditCluster(cluster.id,
        { read: true, "read_reason": "marked" }));
      dispatch(changeReadCount(
        { feedsId: cluster["feeds_id"], action: "read" }));
    }
  },
  collapseCluster() {
    dispatch(removeClusterSelection());
  },
  toggleReadSwipe(cluster) {
    const payload = { feedsId: cluster["feeds_id"] };
    if (cluster.read) {
      dispatch(doEditCluster(cluster.id, { read: false, "read_reason": null }));
      dispatch(changeReadCount({ ...payload, action: "unread" }));
    } else {
      dispatch(doEditCluster(cluster.id, { read: true, "read_reason": "marked" }));
      dispatch(changeReadCount({ ...payload, action: "read" }));
    }
  },
  toggleLikedSwipe(cluster) {
    dispatch(doEditCluster(cluster.id, { liked: !cluster.liked }));
  },
  readOnRedirect(e, cluster) {
    e.stopPropagation();
    if (!cluster.read) {
      // setting a slight timeout so that event loop isn't broken by
      // the mark as read action (which would prevent link opening only with
      // middle click)
      const mark = () => dispatch(doEditCluster(
          cluster.id, { read: true, "read_reason": "consulted" }));
      if(e.type === "mouseup") {
        setTimeout(mark, 1);
      } else {
        mark();
      }
      dispatch(changeReadCount({
        feedsId: cluster["feeds_id"],
        action: "read" }));
    }
  },
});

const Cluster = ({ index, cluster, loadedCluster,
                   icons, doShow, expanded, showContent, unreadOnClose,
                   readOnRedirect, toggleRead, toggleLiked,
                   handleClickOnPanel, isMobileView,
                   markAsRead, collapseCluster,
                   toggleReadSwipe, toggleLikedSwipe,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { feedId, categoryId } = useParams();
  const accordionRef = useRef(null);
  const [dismissing, setDismissing] = useState(false);
  const dismissWrapRef = useRef(null);
  const dismissCtx = useRef(null);
  dismissCtx.current = { cluster, expanded, markAsRead, collapseCluster };

  // Scroll to cluster title when expanded on mobile
  useEffect(() => {
    if (expanded && isMobileView && showContent && accordionRef.current) {
      setTimeout(() => {
        const element = accordionRef.current;
        const elementRect = element.getBoundingClientRect();
        const absoluteElementTop = elementRect.top + window.pageYOffset;
        const offset = appBarHeight + 8;
        const scrollPosition = absoluteElementTop - offset;

        window.scrollTo({
          top: scrollPosition,
          behavior: "smooth"
        });
      }, 100);
    }
  }, [expanded, isMobileView, showContent]);

  // Safety net: force Collapse to full height when content loads.
  // On mobile, the MUI Collapse transition can get stuck at spinner height
  // when the content swaps from CircularProgress to Articles mid-animation.
  useEffect(() => {
    if (expanded && showContent && isMobileView && accordionRef.current) {
      requestAnimationFrame(() => {
        if (!accordionRef.current) return;
        const collapseEl = accordionRef.current.querySelector('.MuiCollapse-root');
        if (collapseEl && collapseEl.style.height !== 'auto') {
          collapseEl.style.height = 'auto';
          collapseEl.style.overflow = 'visible';
        }
      });
    }
  }, [expanded, showContent, isMobileView]);

  const navigateBack = useCallback(() => {
    if (feedId) {
      navigate(`/feed/${feedId}`);
    } else if (categoryId) {
      navigate(`/category/${categoryId}`);
    } else {
      navigate(`/`);
    }
  }, [feedId, categoryId, navigate]);

  // Dismiss animation: slide out then mark as read (unread filter only).
  // The Redux update is deferred until after the animation so showCluster
  // keeps the component mounted during the transition.
  useEffect(() => {
    if (!dismissing) return;
    const el = dismissWrapRef.current;
    if (el) {
      el.style.overflow = "hidden";
      el.style.maxHeight = el.scrollHeight + "px";
      el.style.transition = "max-height 0.25s ease-out 0.1s";
      void el.offsetHeight;
      el.style.maxHeight = "0";
    }
    const timer = setTimeout(() => {
      const ctx = dismissCtx.current;
      if (!ctx.cluster) return;
      ctx.markAsRead(ctx.cluster);
      if (ctx.expanded) {
        ctx.collapseCluster();
        navigateBack();
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [dismissing, navigateBack]);

  const { ref: swipeRef, swipeOffset, isSwiping } = useSwipeGesture({
    onSwipeLeft: () => {
      if (!cluster) return;
      if (unreadOnClose) {
        // Unread filter: animate slide-out, then mark as read
        setDismissing(true);
      } else {
        // Other filters: just toggle read, cluster stays visible
        toggleReadSwipe(cluster);
      }
    },
    onSwipeRight: () => {
      if (!cluster) return;
      toggleLikedSwipe(cluster);
    },
    enabled: isMobileView,
  });
  // Icon transition starts after 40px of swipe, completes at 80px
  const swipeProgress = Math.max(0, Math.min((Math.abs(swipeOffset) - 40) / 40, 1));

  if(!doShow) { return null; }
  let content;
  if(isMobileView && expanded) {
    if (showContent) {
      content = <Articles />;
    } else {
      content = <div className={classes.loadingWrap}><CircularProgress /></div>;
    }
    content = (
      <AccordionDetails className={classes.content}
                             key={`cl-${cluster.id}`}>
        {content}
      </AccordionDetails>
    );
  }

  const accordionElement = (
      <Accordion
        ref={accordionRef}
        expanded={expanded}
        elevation={expanded ? 8 : (isMobileView ? 0 : 2)}
        square={isMobileView && !expanded}
        className={isMobileView ? clsx({[classes.clusterFolded]: !expanded, [classes.clusterExpanded]: expanded}) : undefined}
        TransitionProps={{ unmountOnExit: true }}
        key={`c-${cluster.id}`}
        onChange={(e) => {
          handleClickOnPanel(e, cluster, unreadOnClose, expanded);
          if (!expanded) {
            if (feedId) {
              navigate(`/feed/${feedId}/cluster/${cluster.id}`);
            } else if (categoryId) {
              navigate(`/category/${categoryId}/cluster/${cluster.id}`);
            } else {
              navigate(`/cluster/${cluster.id}`);
            }
          } else {
            navigateBack();
          }
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          key={`cs-${cluster.id}`}
          className={classes.summary}
        >
          <div className={classes.link}>
            <Link href={cluster["main_link"]} target="_blank" color="secondary"
              onMouseUp={(e) => {
                // only middle click
                if(e.button === 1) {readOnRedirect(e, cluster)}
              }}
              onClick={(e) => readOnRedirect(e, cluster)}>
              {[ ...new Set(cluster["feeds_id"])].filter((feedId) => icons[feedId])
                      .map((feedId) => <ClusterIcon
                                          key={`i${cluster.id}f${feedId}`}
                                          iconUrl={icons[feedId]} />
                           )
              }
             {cluster["main_feed_title"]}
            </Link>
            <span className={classes.clusterDate}>{formatDistanceToNow(new Date(cluster["main_date"]), { addSuffix: true })}</span>
          </div>
          <div>
            {!isMobileView ? (
              <>
                <Checkbox checked={cluster.read} key={`c${cluster.id}r`}
                  className={classes.titleAction}
                  name="read" size="small" color="primary"
                  aria-label="toggle read"
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  onChange={(e) => toggleRead(e, cluster)} />
                <Checkbox checked={cluster.liked} key={`c${cluster.id}l`}
                  className={classes.titleAction}
                  name="liked" size="small" color="primary"
                  aria-label="toggle liked"
                  icon={<LikedIconBorder />} checkedIcon={<LikedIcon />}
                  onClick={(e) => e.stopPropagation()}
                  onFocus={(e) => e.stopPropagation()}
                  onChange={(e) => toggleLiked(e, cluster.id)} />
              </>
            ) : (
              <>
                {cluster.read
                  ? <CheckBoxIcon className={classes.mobileStatusIcon} />
                  : <CheckBoxOutlineBlankIcon className={classes.mobileStatusIcon} />}
                {cluster.liked
                  ? <LikedIcon className={classes.mobileStatusIcon} />
                  : <LikedIconBorder className={classes.mobileStatusIcon} />}
              </>
            )}
            <Typography
               className={clsx(classes.mainTitle, {[classes.mainTitleExpanded]: expanded})}>
             {cluster["main_title"]}
            </Typography>
          </div>
        </AccordionSummary>
        {content}
      </Accordion>
  );

  if (isMobileView) {
    const readFromIcon = cluster.read ? <CheckBoxIcon /> : <CheckBoxOutlineBlankIcon />;
    const readToIcon = cluster.read ? <CheckBoxOutlineBlankIcon /> : <CheckBoxIcon />;
    const likedFromIcon = cluster.liked ? <LikedIcon /> : <LikedIconBorder />;
    const likedToIcon = cluster.liked ? <LikedIconBorder /> : <LikedIcon />;

    return (
      <div ref={dismissWrapRef}>
        <div className={clsx(classes.swipeContainer,
              {[classes.swipeContainerActive]: isSwiping || dismissing})}
             ref={swipeRef}>
          {isSwiping && !dismissing && (
            <div className={clsx(classes.swipeBg,
              swipeOffset < 0 ? classes.swipeBgRead : classes.swipeBgLiked)}>
              <div style={{ display: "grid" }}>
                <div style={{
                  gridArea: "1/1",
                  opacity: 1 - swipeProgress,
                  transform: `scale(${1 - swipeProgress * 0.4})`,
                  transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
                }}>
                  {swipeOffset < 0 ? readFromIcon : likedFromIcon}
                </div>
                <div style={{
                  gridArea: "1/1",
                  opacity: swipeProgress,
                  transform: `scale(${0.6 + swipeProgress * 0.4})`,
                  transition: "opacity 0.6s ease-out, transform 0.6s ease-out",
                }}>
                  {swipeOffset < 0 ? readToIcon : likedToIcon}
                </div>
              </div>
            </div>
          )}
          <div className={classes.swipeContent}
               style={{
                 transform: dismissing
                   ? "translateX(-100%)"
                   : `translateX(${swipeOffset}px)`,
                 opacity: dismissing ? 0 : 1,
                 transition: dismissing
                   ? "transform 0.3s ease-out, opacity 0.25s ease-out"
                   : (isSwiping ? "none" : "transform 0.15s ease-out"),
               }}>
            {accordionElement}
          </div>
        </div>
      </div>
    );
  }

  return accordionElement;
};

Cluster.propTypes = {
  index: PropTypes.number.isRequired,
  cluster: PropTypes.shape({
    id: PropTypes.number.isRequired,
    read: PropTypes.bool.isRequired,
    liked: PropTypes.bool.isRequired,
    "feeds_id": PropTypes.array.isRequired,
    "main_title": PropTypes.string.isRequired,
    "main_link": PropTypes.string.isRequired,
    "main_feed_title": PropTypes.string.isRequired,
    "main_date": PropTypes.string.isRequired,
  }), // Can be null during loading transitions
  icons: PropTypes.object.isRequired,
  unreadOnClose: PropTypes.bool.isRequired,
  expanded: PropTypes.bool.isRequired,
  isMobileView: PropTypes.bool.isRequired,
  showContent: PropTypes.bool.isRequired,
  doShow: PropTypes.bool.isRequired,
  // funcs
  readOnRedirect: PropTypes.func.isRequired,
  toggleRead: PropTypes.func.isRequired,
  toggleLiked: PropTypes.func.isRequired,
  handleClickOnPanel: PropTypes.func.isRequired,
  markAsRead: PropTypes.func.isRequired,
  collapseCluster: PropTypes.func.isRequired,
  toggleReadSwipe: PropTypes.func.isRequired,
  toggleLikedSwipe: PropTypes.func.isRequired,
};

export default connect(makeMapStateToProps, mapDispatchToProps)(Cluster);
